export type ParticleColorMode = "A" | "B" | "W";

export interface ParticleSettings {
    density: number;
    speed: number;
    age: number;
    length: number;
    lineWidth: number;
    colorThreshold: number;
    colorMode: ParticleColorMode;
}

export interface ParticleField {
    u: Int16Array;
    v: Int16Array;
    projection: Uint8Array;
    width: number;
    height: number;
    timeCount: number;
    startFt: number;
    endFt: number;
}

export interface ParticleGeometry {
    gridDx: number;
    gridDy: number;
    scale: number;
    rx: number;
    ry: number;
    rw: number;
    rh: number;
    rx0: number;
    ry0: number;
    degree: number;
    backWidth: number;
    backHeight: number;
}

export interface ParticleRenderer {
    readonly kind: "webgl" | "2d";
    configure(
        field: ParticleField,
        geometry: ParticleGeometry,
        settings: ParticleSettings,
    ): void;
    renderTo(ft: number): void;
    step(): number;
    present(): void;
    reset(): void;
    dispose(): void;
    getCurrentFt(): number;
}

export const PARTICLE_COLORS: Record<
    ParticleColorMode,
    [number, number, number][]
> = {
    A: [
        [0, 255, 255],
        [0, 255, 128],
        [128, 255, 0],
        [255, 255, 0],
        [255, 221, 0],
        [255, 170, 0],
        [255, 85, 0],
    ],
    B: [
        [204, 217, 204],
        [204, 234, 204],
        [128, 255, 0],
        [255, 255, 0],
        [255, 221, 0],
        [255, 170, 0],
        [255, 85, 0],
    ],
    W: Array.from({ length: 7 }, () => [234, 234, 234]),
};

export const PARTICLE_STEPS_PER_FT = 10;

// トレイルのフェード (1ステップ毎に 10^(-1/長さ^1.5) 倍) が 1/512 未満に減衰する
// ステップ数。これより古い描画は視覚的に寄与しないため、長距離の renderTo
// (動画チャンクのウォームアップ等) ではこの窓の外の描画をスキップできる。
// 位置・乱数列は全ステップ計算するので粒子状態は完全に同一。
export function warmupWindowSteps(settings: ParticleSettings) {
    return (
        Math.ceil(Math.log10(512) * Math.pow(settings.length, 1.5)) +
        PARTICLE_STEPS_PER_FT
    );
}
export const PARTICLE_START_COUNT = -30;
export const PARTICLE_WIDTH = 1920;
export const PARTICLE_HEIGHT = 1080;

export function particleCount(
    field: ParticleField,
    geometry: ParticleGeometry,
    density: number,
) {
    let valid = 0;
    const n = field.width * field.height;
    for (let index = 0; index < n; index++) {
        if (field.u[index] !== 32767) {
            valid++;
        }
    }
    return Math.max(
        1,
        Math.floor(
            (density / 1000) *
                PARTICLE_WIDTH *
                PARTICLE_HEIGHT *
                (valid / n) *
                (Math.pow(field.width * geometry.gridDx, 1.3) / 48000),
        ),
    );
}

export function seededRandom(seed = 0x77504d41) {
    let state = seed >>> 0;
    return () => {
        state += 0x6d2b79f5;
        let value = state;
        value = Math.imul(value ^ (value >>> 15), value | 1);
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
        return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
}
